import {ProvisionRunStatus} from '../enum/provision-run-status.enum';
import {CP_RUN_STATUS_ORDER} from '../enum/provision-run-status-order';
import {CP_RUN_STATUS_LABEL_UNKNOWN, CP_RUN_STATUS_LABELS} from '../enum/provision-run-status-label';

/**
 * How ONE ProvisionRunStatus is presented: label, chip colours and icon.
 *
 * Direct mirror of the bm reference `features/feature-bm/bm/model/billing-run-status-presentation.ts`
 * (PAT §J.2) — same interface, same colour-token vocabulary, same lookup helper. Shared on purpose:
 * the dashboard start page, the runs table and the run-detail step bar all render a status chip; a
 * second private copy of this table is how they would start disagreeing about what a status is.
 *
 * ── THE LABEL IS NOT OWNED HERE
 *
 * This file used to carry its own German label column, and it disagreed with
 * `enum/provision-run-status-label.ts` on IN_CALCULATION / IN_ACTIVATION / IN_PAYOUT — the same run
 * was called "Berechnung läuft" on the dashboard and "In Berechnung" in the run-detail header. The
 * labels now come from {@link CP_RUN_STATUS_LABELS}, the single naming source (its file TSDoc
 * carries the reasoning for the chosen wording, 04-frontend.md §5.1). What is left here is what
 * this file is actually about: the ICON and the COLOUR of a status chip.
 */
export interface ProvisionRunStatusPresentation {
    label: string;
    icon: string;
    text: string;
    background: string;
}

/**
 * Icon + colour per status; the label is read from the shared label map (H4/H2: German UI text with
 * real umlauts, English code identifiers — the UI reads "Provision…" and never "Cp").
 *
 * Icons are Font Awesome (`fa-solid`), never PrimeIcons — the house icon set (UX7,
 * fe_core_ux-guidelines).
 */
export const PROVISION_RUN_STATUS_PRESENTATION: { [status: number]: ProvisionRunStatusPresentation } = {
    [ProvisionRunStatus.NEW]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.NEW], icon: 'fa-solid fa-circle-plus', text: 'text-blue-800', background: 'bg-blue-100'},
    [ProvisionRunStatus.IN_CALCULATION]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.IN_CALCULATION], icon: 'fa-solid fa-spinner fa-spin', text: 'text-orange-800', background: 'bg-orange-100'},
    [ProvisionRunStatus.CALCULATED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.CALCULATED], icon: 'fa-solid fa-calculator', text: 'text-yellow-800', background: 'bg-yellow-100'},
    [ProvisionRunStatus.IN_REVIEW]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.IN_REVIEW], icon: 'fa-solid fa-magnifying-glass-chart', text: 'text-teal-800', background: 'bg-teal-100'},
    [ProvisionRunStatus.IN_ACTIVATION]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.IN_ACTIVATION], icon: 'fa-solid fa-spinner fa-spin', text: 'text-cyan-800', background: 'bg-cyan-100'},
    [ProvisionRunStatus.ACTIVATED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.ACTIVATED], icon: 'fa-solid fa-circle-check', text: 'text-cyan-800', background: 'bg-cyan-100'},
    [ProvisionRunStatus.IN_PAYOUT]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.IN_PAYOUT], icon: 'fa-solid fa-spinner fa-spin', text: 'text-purple-800', background: 'bg-purple-100'},
    [ProvisionRunStatus.PAID_OUT]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.PAID_OUT], icon: 'fa-solid fa-money-bill-transfer', text: 'text-purple-800', background: 'bg-purple-100'},
    [ProvisionRunStatus.COMPLETED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.COMPLETED], icon: 'fa-solid fa-flag-checkered', text: 'text-green-800', background: 'bg-green-100'},
    [ProvisionRunStatus.CALCULATION_FAILED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.CALCULATION_FAILED], icon: 'fa-solid fa-triangle-exclamation', text: 'text-red-800', background: 'bg-red-100'},
    [ProvisionRunStatus.ACTIVATION_FAILED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.ACTIVATION_FAILED], icon: 'fa-solid fa-triangle-exclamation', text: 'text-red-800', background: 'bg-red-100'},
    [ProvisionRunStatus.PAYOUT_FAILED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.PAYOUT_FAILED], icon: 'fa-solid fa-triangle-exclamation', text: 'text-red-800', background: 'bg-red-100'},
    [ProvisionRunStatus.ABORTED]: {label: CP_RUN_STATUS_LABELS[ProvisionRunStatus.ABORTED], icon: 'fa-solid fa-ban', text: 'text-700', background: 'bg-100'}
};

export const PROVISION_RUN_STATUS_UNKNOWN: ProvisionRunStatusPresentation = {
    label: CP_RUN_STATUS_LABEL_UNKNOWN, icon: 'fa-solid fa-question', text: 'text-700', background: 'bg-100'
};

export function provisionRunStatusPresentation(status: number | undefined | null): ProvisionRunStatusPresentation {
    if (status == null) {
        return PROVISION_RUN_STATUS_UNKNOWN;
    }
    return PROVISION_RUN_STATUS_PRESENTATION[status] || PROVISION_RUN_STATUS_UNKNOWN;
}

/**
 * Which BUSINESS step a FAILED status belongs to (04-frontend.md §5.2).
 *
 * The three *_FAILED statuses carry ordinals 9/10/11 because they were appended last, but they are
 * reached FROM a business step and must be reported AT that step. Without this anchor a failed run
 * would fall out of CP_RUN_STATUS_ORDER (indexOf = -1) and be rendered at 0 % progress — i.e. a run
 * that failed during payout would look like it had never started.
 *
 * ABORTED is deliberately NOT in this map: it is terminal and belongs to no step (04 §5.2), so it
 * resolves to -1 and the terminal banner carries the whole statement.
 *
 * This is the ONE anchor of the module. {@code CpProvisionRunStatusFlowComponent} imports it for
 * its step derivation instead of keeping a private copy — two anchors that disagree would place a
 * failed run on two different steps in the progress chip and in the step bar of the same page.
 */
export const PROVISION_RUN_STATUS_FAILED_ANCHOR: { [status: number]: ProvisionRunStatus } = {
    [ProvisionRunStatus.CALCULATION_FAILED]: ProvisionRunStatus.IN_CALCULATION,
    [ProvisionRunStatus.ACTIVATION_FAILED]: ProvisionRunStatus.IN_ACTIVATION,
    [ProvisionRunStatus.PAYOUT_FAILED]: ProvisionRunStatus.IN_PAYOUT
};

/**
 * How far the run has progressed, in percent, along the BUSINESS order CP_RUN_STATUS_ORDER —
 * NEVER along the enum ordinal (04-frontend.md §5.2, U-09; DA-017.6 tests against this list).
 *
 * A FAILED status reports the progress of the step it failed in; ABORTED is terminal and outside
 * the bar, so it reports 0 and is rendered as a banner instead of a progress state.
 */
export function provisionRunStatusProgressPercent(status: number | undefined | null): number {
    if (status == null || status === ProvisionRunStatus.ABORTED) {
        return 0;
    }

    const anchored: ProvisionRunStatus = PROVISION_RUN_STATUS_FAILED_ANCHOR[status] ?? status;
    const index: number = CP_RUN_STATUS_ORDER.indexOf(anchored);

    if (index < 0) {
        return 0;
    }

    return Math.round(((index + 1) / CP_RUN_STATUS_ORDER.length) * 100);
}

/** A run resting in one of the "… läuft" states is being worked on by a background job right now. */
export function provisionRunStatusIsRunning(status: number | undefined | null): boolean {
    return status === ProvisionRunStatus.IN_CALCULATION
        || status === ProvisionRunStatus.IN_ACTIVATION
        || status === ProvisionRunStatus.IN_PAYOUT;
}

export function provisionRunStatusIsFailed(status: number | undefined | null): boolean {
    return status === ProvisionRunStatus.CALCULATION_FAILED
        || status === ProvisionRunStatus.ACTIVATION_FAILED
        || status === ProvisionRunStatus.PAYOUT_FAILED;
}

export function provisionRunStatusIsCompleted(status: number | undefined | null): boolean {
    return status === ProvisionRunStatus.COMPLETED;
}

export function provisionRunStatusIsAborted(status: number | undefined | null): boolean {
    return status === ProvisionRunStatus.ABORTED;
}
